import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../firebase";
import { Box, Typography, Alert, Button } from "@mui/material";
import { DevAdminUtils } from "../utils/adminUtils";

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, isAdmin } = useAuth();

  // If not logged in, redirect to sign in
  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  // If not admin, show access denied with dev toggle option
  if (!isAdmin) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          p: 4,
        }}
      >
        <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" gutterBottom>
            You don't have permission to access the admin dashboard.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Admin access is restricted to authorized users only.
          </Typography>
        </Alert>

        {/* Development Toggle for Testing */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            For development/testing purposes:
          </Typography>
          <Button
            variant="outlined"
            color={DevAdminUtils.isAdminModeEnabled() ? "success" : "primary"}
            onClick={() => {
              DevAdminUtils.toggleAdminMode();
              window.location.reload();
            }}
            sx={{ mt: 1 }}
          >
            {DevAdminUtils.isAdminModeEnabled() ? "Disable" : "Enable"} Admin
            Mode
          </Button>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 1, color: "text.secondary" }}
          >
            This toggle is for development only and will be removed in
            production
          </Typography>
        </Box>
      </Box>
    );
  }

  // If admin, render the protected content
  return <>{children}</>;
};

export default AdminRoute;
